/**
 * Help (2026-10-05; a Navigate section since 2026-10-06): answers, the three
 * ways to tell us something, My reports and What's new.
 *
 * WHY IN THE APP AND NOT A LINK. The help site (support.overpoweredapps.net; `help.` until 2026-10-08)
 * builds its pages from the same `help/*.md` files, but a link needs the
 * internet, and the moment someone needs help is often at a venue without it.
 * These articles ride in the app bundle, so they open offline. Reports go
 * through the outbox (`diag/reportQueue.ts`), which keeps them until they can
 * be sent.
 *
 * THE FORMAT (operator, 2026-10-06: "make sure all future opbuttons ui's are
 * in the new format"). Built ONLY from the Navigate kit (`nv-*` and the
 * `nav-well` rows in skin-components.css, from app-study.html): sections with
 * a plain heading, rows in a well, quiet buttons, a segmented choice. It was
 * a skinned window over Navigate, in the display face and mono. The skin is
 * for the stage; every screen off it is drawn like this.
 *
 * THREE WAYS IN, NOT ONE FORM WITH A TYPE CHIP (operator, 2026-10-06). A
 * problem, an idea and a question want different words: "you'll hear when
 * it's fixed" is wrong for a question. Each opens its own form with its own
 * title, prompt and promise; underneath they are one op.report with a
 * `type`, so My reports keeps them in one list. The words the SERVER says
 * back (received / fixed / answered) are OPApps' (op-report.md).
 *
 * The view stack (`help/store.ts`) gives Back; the section's head shows the
 * view's title, set in Navigate's PANE_DEFS from `helpHead()` below.
 */

import { For, Show, Switch, Match, createSignal, createResource, createMemo, createEffect, on, type JSX } from 'solid-js';
import { helpView, helpGo, helpBack, type HelpView } from '../help/store.ts';
import {
  HELP_SECTIONS, SECTION_TITLES, articlesIn, helpArticle, helpUrl, searchHelp, WHATS_NEW,
} from '../help/content.ts';
import { Markdown } from '../help/Markdown.tsx';
import { buildDiagReport, sendDiagReport } from '../diag/report.ts';
import { toOpReport, type ReportType } from '../diag/opReport.ts';
import { submitReport, outboxCount, outboxReason, retryOutbox, type SendOutcome, type WaitReason } from '../diag/reportQueue.ts';
import {
  listMyReports, openMyReport, replyToMyReport, deleteMyReport, myReportsUnread,
} from '../diag/helpDesk.ts';

const WAIT_TEXT: Record<WaitReason, string> = {
  signin: 'It will send once you are signed in to OverpoweredApps on this device.',
  offline: 'There is no internet right now. It will send by itself when there is.',
  busy: 'OverpoweredApps could not take it just now. It will try again by itself.',
  performing: 'The suite is in performance mode. It will send when the show is over.',
};

const DESK_TROUBLE: Record<'signin' | 'offline' | 'error', string> = {
  signin: 'Sign in to OverpoweredApps on this device to see your reports.',
  offline: 'OverpoweredApps cannot be reached right now. Your reports are safe; look again when you are online.',
  error: 'Your reports could not be loaded just now. Try again in a minute.',
};

/** The three ways in. `problem` covers two report types, chosen in its form. */
type Kind = 'problem' | 'idea' | 'question';
const kindOf = (t: ReportType | undefined): Kind => (t === 'idea' ? 'idea' : t === 'question' ? 'question' : 'problem');

const KINDS: Record<Kind, { title: string; desc: string; tile: string; hint: string; send: string; diagnostics: boolean }> = {
  problem: {
    title: 'Report a problem',
    desc: 'Something froze or did the wrong thing. Tell us what happened; what OPButtons recorded goes with it.',
    tile: 'It froze, or did the wrong thing',
    hint: 'What did you do, what happened, and what did you expect?',
    send: 'Send report',
    diagnostics: true,
  },
  idea: {
    title: 'Suggest an idea',
    desc: 'Something you wish OPButtons did. Ideas are read, and you hear back here.',
    tile: 'Something you wish it did',
    hint: 'What would you like OPButtons to do? What would it help you with?',
    send: 'Send idea',
    diagnostics: false,
  },
  question: {
    title: 'Ask a question',
    desc: 'Anything about OPButtons. The answer comes back in My reports.',
    tile: 'The answer comes back here',
    hint: 'Ask anything.',
    send: 'Send question',
    diagnostics: false,
  },
};

/** The section head for the view on top: Navigate's PANE_DEFS reads this. */
export function helpHead(): { title: string; desc: string } {
  return headOf(helpView());
}

/** The head of any view: also Back's label ("‹ My reports"). */
export function headOf(v: HelpView): { title: string; desc: string } {
  switch (v.v) {
    case 'article': {
      const a = helpArticle(v.slug);
      return { title: a?.title ?? 'Help', desc: a?.summary ?? '' };
    }
    case 'report': {
      const k = KINDS[kindOf(v.type)];
      return { title: k.title, desc: k.desc };
    }
    case 'reports': return { title: 'My reports', desc: 'What you sent, and what happened to it.' };
    case 'report-detail': return { title: 'Report', desc: '' };
    case 'whats-new': return { title: "What's new", desc: 'What changed in each version.' };
    default: return { title: 'Help', desc: 'Answers in plain words, and three ways to tell us something.' };
  }
}

export function HelpPane(): JSX.Element {
  // Each view opens at its top, as a Navigate section does: an answer tapped
  // low in the list must not open scrolled to where the list was.
  let el: HTMLDivElement | undefined;
  createEffect(on(helpView, () => {
    el?.closest('.logo-menu-body')?.scrollTo(0, 0);
    el?.closest('.logo-menu-main')?.scrollTo(0, 0);
  }, { defer: true }));
  return (
    <div class="nav-pane-body nv-help" role="region" aria-label="Help" ref={(e) => (el = e)}>
      <Switch>
        <Match when={helpView().v === 'home'}><HelpHome /></Match>
        <Match when={helpView().v === 'article'}><ArticleView slug={(helpView() as { slug: string }).slug} /></Match>
        <Match when={helpView().v === 'report'}>
          <ReportForm initialType={(helpView() as { type?: ReportType }).type} page={(helpView() as { page?: string }).page} />
        </Match>
        <Match when={helpView().v === 'reports'}><MyReports /></Match>
        <Match when={helpView().v === 'report-detail'}><MyReportDetail id={(helpView() as { id: string }).id} /></Match>
        <Match when={helpView().v === 'whats-new'}><WhatsNew /></Match>
      </Switch>
    </div>
  );
}

function openArticle(slug: string): void {
  if (helpArticle(slug)) helpGo({ v: 'article', slug });
}

function Chev(): JSX.Element {
  return <span class="nav-lr-chev" aria-hidden="true">›</span>;
}

function HelpHome(): JSX.Element {
  const [q, setQ] = createSignal('');
  const results = createMemo(() => searchHelp(q()));
  const ways: Array<{ kind: Kind; type: ReportType }> = [
    { kind: 'problem', type: 'bug' },
    { kind: 'idea', type: 'idea' },
    { kind: 'question', type: 'question' },
  ];
  return (
    <>
      <label class="nv-fld nv-help-search">
        <input
          type="search"
          placeholder="What do you need help with?"
          aria-label="Search help"
          value={q()}
          onInput={(e) => setQ(e.currentTarget.value)}
        />
      </label>
      <Show when={q().trim().length > 1}>
        <section class="nv-ss help-results">
          <div class="nv-ss-h"><h2>Answers</h2></div>
          <Show when={results().length > 0} fallback={<p class="nav-empty">Nothing matches "{q().trim()}". Try other words, or ask below.</p>}>
            <div class="nav-well">
              <For each={results()}>{(a) => <ArticleRow slug={a.slug} title={a.title} summary={a.summary} />}</For>
            </div>
          </Show>
        </section>
      </Show>

      <section class="nv-ss">
        <div class="nv-ss-h"><h2>Tell us something</h2><p>It goes to the OverpoweredApps team, and you hear back here.</p></div>
        <div class="nv-grid nv-g3">
          <For each={ways}>
            {(w) => (
              <button type="button" class="nv-qa help-way" data-kind={w.kind} onClick={() => helpGo({ v: 'report', type: w.type })}>
                <b>{KINDS[w.kind].title}</b>
                <small>{KINDS[w.kind].tile}</small>
              </button>
            )}
          </For>
        </div>
        <div class="nav-well nv-help-mine">
          <button type="button" class="nav-lr nav-lr-button help-my-reports" onClick={() => helpGo({ v: 'reports' })}>
            <span class="nav-lr-main"><b>My reports</b><small>What you sent, and what happened to it</small></span>
            <span class="nav-lr-trail">
              <Show when={myReportsUnread() > 0}><span class="nv-ct help-badge" data-s="live" aria-label={`${myReportsUnread()} new`}>{myReportsUnread()} new</span></Show>
              <Chev />
            </span>
          </button>
        </div>
        <OutboxLine />
      </section>

      <For each={HELP_SECTIONS}>
        {(section) => (
          <Show when={articlesIn(section).length > 0}>
            <section class="nv-ss help-sect">
              <div class="nv-ss-h"><h2>{SECTION_TITLES[section]}</h2></div>
              <div class="nav-well">
                <For each={articlesIn(section)}>{(a) => <ArticleRow slug={a.slug} title={a.title} summary={a.summary} />}</For>
              </div>
            </section>
          </Show>
        )}
      </For>

      <section class="nv-ss">
        <div class="nv-ss-h"><h2>More</h2></div>
        <div class="nav-well">
          <Show when={WHATS_NEW}>
            <button type="button" class="nav-lr nav-lr-button" onClick={() => helpGo({ v: 'whats-new' })}>
              <span class="nav-lr-main"><b>What's new</b><small>What changed in each version of OPButtons</small></span>
              <Chev />
            </button>
          </Show>
          <a class="nav-lr nav-lr-button help-web" href="https://support.overpoweredapps.net/opbuttons" target="_blank" rel="noopener noreferrer">
            <span class="nav-lr-main"><b>Open the help site</b><small>support.overpoweredapps.net - needs the internet</small></span>
            <Chev />
          </a>
        </div>
      </section>
    </>
  );
}

function ArticleRow(props: { slug: string; title: string; summary: string }): JSX.Element {
  return (
    <button type="button" class="nav-lr nav-lr-button help-row" data-slug={props.slug} onClick={() => openArticle(props.slug)}>
      <span class="nav-lr-main"><b>{props.title}</b><Show when={props.summary}><small>{props.summary}</small></Show></span>
      <Chev />
    </button>
  );
}

function ArticleView(props: { slug: string }): JSX.Element {
  const article = () => helpArticle(props.slug);
  return (
    <Show when={article()} fallback={<p class="nav-empty">That answer is not in this version of OPButtons.</p>}>
      {(a) => (
        <article class="help-article nv-prose" data-slug={a().slug}>
          <Markdown source={a().body} onHelpLink={openArticle} />
          <div class="nv-row help-article-foot">
            <button type="button" class="nv-b" onClick={() => helpGo({ v: 'report', type: 'bug', page: a().slug })}>Still stuck? Report a problem</button>
            <button type="button" class="nv-b" onClick={() => helpGo({ v: 'report', type: 'question', page: a().slug })}>Ask a question</button>
            <a class="nv-b nv-b-q help-link" href={helpUrl(a().slug)} target="_blank" rel="noopener noreferrer">Open on the web</a>
          </div>
        </article>
      )}
    </Show>
  );
}

function WhatsNew(): JSX.Element {
  return (
    <Show when={WHATS_NEW} fallback={<p class="nav-empty">No release notes in this version.</p>}>
      {(a) => (
        <article class="help-article nv-prose">
          <Markdown source={a().body} onHelpLink={openArticle} />
        </article>
      )}
    </Show>
  );
}

function OutboxLine(): JSX.Element {
  return (
    <Show when={outboxCount() > 0}>
      <p class="nv-note help-outbox" data-tone="info" data-testid="help-outbox">
        <span>
          {outboxCount() === 1 ? '1 report is waiting to send. ' : `${outboxCount()} reports are waiting to send. `}
          {outboxReason() ? WAIT_TEXT[outboxReason()!] : ''}
        </span>
        <button type="button" class="nv-b nv-b-sm" onClick={() => void retryOutbox()}>Try now</button>
      </p>
    </Show>
  );
}

function ReportForm(props: { initialType?: ReportType | undefined; page?: string | undefined }): JSX.Element {
  const kind = kindOf(props.initialType);
  const k = KINDS[kind];
  const [type, setType] = createSignal<ReportType>(kind === 'problem' ? (props.initialType === 'freeze' ? 'freeze' : 'bug') : (props.initialType as ReportType));
  const [note, setNote] = createSignal('');
  const [diagnostics, setDiagnostics] = createSignal(k.diagnostics);
  const [copyLocal, setCopyLocal] = createSignal(false);
  const [busy, setBusy] = createSignal(false);
  const [outcome, setOutcome] = createSignal<SendOutcome | null>(null);
  const [localNote, setLocalNote] = createSignal('');

  const send = async () => {
    setBusy(true);
    const v0 = buildDiagReport();
    if (copyLocal()) {
      const r = await sendDiagReport(v0);
      setLocalNote(r.sentTo.length ? `A copy was saved on ${r.sentTo.join(' and ')}.` : 'No OPBridge was connected, so no copy was saved on your computer.');
    }
    const out = toOpReport(v0, { type: type(), note: note(), sent: 'manual', diagnostics: diagnostics(), ...(props.page ? { page: props.page } : {}) });
    setOutcome(await submitReport(out));
    setBusy(false);
  };

  return (
    <Show
      when={!outcome()}
      fallback={
        <div class="help-sent" data-testid="report-outcome" data-kind={outcome()!.kind}>
          <div class="nv-note" data-tone={outcome()!.kind === 'dropped' ? 'warn' : 'info'}>
            <Switch>
              <Match when={outcome()!.kind === 'sent'}><span>{(outcome() as { message: string }).message}</span></Match>
              <Match when={outcome()!.kind === 'queued'}>
                <span>Saved on this device. {WAIT_TEXT[(outcome() as { reason: WaitReason }).reason]}</span>
              </Match>
              <Match when={outcome()!.kind === 'dropped'}>
                <span>This could not be sent: {(outcome() as { reason: string }).reason} You can still use Settings, then "Copy", and paste it to us.</span>
              </Match>
            </Switch>
          </div>
          <Show when={localNote()}><p class="nav-empty">{localNote()}</p></Show>
          <div class="nv-row">
            <button type="button" class="nv-b nv-b-pri" onClick={() => helpGo({ v: 'reports' })}>My reports</button>
            <button type="button" class="nv-b" onClick={helpBack}>Done</button>
          </div>
        </div>
      }
    >
      <div class="help-report" data-kind={kind}>
        <Show when={kind === 'problem'}>
          <section class="nv-ss">
            <div class="nv-ss-h"><h2>What happened</h2></div>
            <div class="nv-seg help-types" role="radiogroup" aria-label="What happened">
              <button type="button" role="radio" aria-checked={type() === 'freeze'} aria-pressed={type() === 'freeze'} data-type="freeze" onClick={() => setType('freeze')}>It froze</button>
              <button type="button" role="radio" aria-checked={type() === 'bug'} aria-pressed={type() === 'bug'} data-type="bug" onClick={() => setType('bug')}>Something is wrong</button>
            </div>
          </section>
        </Show>
        <section class="nv-ss">
          <div class="nv-ss-h"><h2>{kind === 'question' ? 'Your question' : kind === 'idea' ? 'Your idea' : 'In your words'}</h2></div>
          <textarea
            class="nv-area help-note"
            rows={5}
            maxLength={4000}
            placeholder={type() === 'freeze' ? 'What were you doing when it stopped responding? What brought it back?' : k.hint}
            aria-label="Your words"
            value={note()}
            onInput={(e) => setNote(e.currentTarget.value)}
          />
        </section>
        <section class="nv-ss">
          <div class="nv-ss-h"><h2>What goes with it</h2></div>
          <div class="nav-well">
            <div class="nav-lr"><span class="nav-lr-main"><b>Your device type and this version of OPButtons</b><small>Always, so we know what you are on</small></span></div>
            <label class="nav-lr nav-lr-button nv-check">
              <span class="nav-lr-main">
                <b>What OPButtons recorded</b>
                <small>Connection history (the last 3 hours, and every drop over the last few weeks), recent error messages, and your OPBridge computers' names and versions</small>
              </span>
              <input type="checkbox" checked={diagnostics()} onChange={(e) => setDiagnostics(e.currentTarget.checked)} />
            </label>
            <Show when={kind === 'problem'}>
              <label class="nav-lr nav-lr-button nv-check">
                <span class="nav-lr-main"><b>Also save a copy on my computer</b><small>On every OPBridge that is connected</small></span>
                <input type="checkbox" checked={copyLocal()} onChange={(e) => setCopyLocal(e.currentTarget.checked)} />
              </label>
            </Show>
          </div>
          <p class="nav-empty help-privacy">Goes to the OverpoweredApps team only. Kept until 90 days after it is closed. You can delete it from My reports.</p>
        </section>
        <div class="nv-row">
          <button type="button" class="nv-b nv-b-pri" data-testid="report-send" disabled={busy() || (note().trim() === '' && !diagnostics())} onClick={() => void send()}>
            {busy() ? 'Sending...' : k.send}
          </button>
          <button type="button" class="nv-b nv-b-q" onClick={helpBack}>Cancel</button>
        </div>
      </div>
    </Show>
  );
}

function MyReports(): JSX.Element {
  const [list, { refetch }] = createResource(listMyReports);
  const rows = () => { const r = list(); return r && r.ok ? r.value.reports : null; };
  const trouble = () => { const r = list(); return r && !r.ok ? DESK_TROUBLE[r.reason] : null; };
  return (
    <div class="help-reports">
      <Show when={list()} fallback={<p class="nav-empty">Loading...</p>}>
        <Show when={rows()} fallback={<><p class="nav-empty">{trouble()}</p><div class="nv-row"><button type="button" class="nv-b" onClick={() => void refetch()}>Try again</button></div></>}>
          <Show when={rows()!.length > 0} fallback={<p class="nav-empty">Nothing sent yet. What you send from OPButtons shows up here, with what happened to it.</p>}>
            <div class="nav-well">
              <For each={rows()!}>
                {(row) => (
                  <button type="button" class="nav-lr nav-lr-button help-report-row" data-unread={row.unread ? 'true' : 'false'} onClick={() => helpGo({ v: 'report-detail', id: row.id })}>
                    <span class="nav-lr-main">
                      <b>{row.title || 'Report'}</b>
                      <small>{row.status_label} · {new Date(row.updated_at || row.received_at).toLocaleDateString()}</small>
                    </span>
                    <span class="nav-lr-trail">
                      <Show when={row.unread}><span class="nv-ct" data-s="live">New</span></Show>
                      <Chev />
                    </span>
                  </button>
                )}
              </For>
            </div>
          </Show>
        </Show>
      </Show>
      <OutboxLine />
    </div>
  );
}

function MyReportDetail(props: { id: string }): JSX.Element {
  const [detail, { refetch }] = createResource(() => props.id, openMyReport);
  const [reply, setReply] = createSignal('');
  const [sending, setSending] = createSignal(false);
  const [confirmDelete, setConfirmDelete] = createSignal(false);
  const [note, setNote] = createSignal('');
  const value = () => { const d = detail(); return d && d.ok ? d.value : null; };
  const trouble = () => { const d = detail(); return d && !d.ok ? DESK_TROUBLE[d.reason] : null; };

  const sendReply = async () => {
    setSending(true);
    const r = await replyToMyReport(props.id, reply());
    setSending(false);
    if (r.ok) { setReply(''); setNote(''); void refetch(); } else setNote(DESK_TROUBLE[r.reason]);
  };
  const remove = async () => {
    if (!confirmDelete()) { setConfirmDelete(true); setTimeout(() => setConfirmDelete(false), 3000); return; }
    const r = await deleteMyReport(props.id);
    if (r.ok) helpBack(); else setNote(DESK_TROUBLE[r.reason]);
  };

  return (
    <Show when={detail()} fallback={<p class="nav-empty">Loading...</p>}>
      <Show when={value()} fallback={<p class="nav-empty">{trouble()}</p>}>
        {(v) => (
          <div class="help-report-detail">
            <section class="nv-ss">
              <div class="nv-ss-h">
                <h2>{String(v().report.title ?? v().report.note ?? 'Report').split('\n')[0]}</h2>
                <span class="nv-sp" />
                <span class="nv-ct">{String(v().report.status_label ?? '')}</span>
              </div>
              <ol class="nav-well help-thread">
                <For each={v().messages}>
                  {(m) => (
                    <li class="nav-lr help-msg" data-from={m.from}>
                      <span class="nav-lr-main">
                        <small class="help-msg-who">
                          {m.from === 'you' ? 'You' : 'OverpoweredApps'} · {new Date(m.at).toLocaleString()}
                          <Show when={m.status_label}><span class="nv-ct help-msg-status">{m.status_label}</span></Show>
                        </small>
                        <span class="help-msg-body">{m.body}</span>
                      </span>
                    </li>
                  )}
                </For>
              </ol>
            </section>
            <section class="nv-ss">
              <div class="nv-ss-h"><h2>Reply</h2></div>
              <textarea class="nv-area help-note" rows={3} maxLength={4000} placeholder="Write a reply" aria-label="Reply" value={reply()} onInput={(e) => setReply(e.currentTarget.value)} />
              <Show when={note()}><p class="nav-empty">{note()}</p></Show>
            </section>
            <div class="nv-row">
              <button type="button" class="nv-b nv-b-pri" disabled={sending() || reply().trim() === ''} onClick={() => void sendReply()}>{sending() ? 'Sending...' : 'Send reply'}</button>
              <button type="button" class="nv-b nv-b-q" data-danger={confirmDelete() ? 'armed' : 'true'} onClick={() => void remove()}>
                {confirmDelete() ? 'Tap again to delete' : 'Delete report'}
              </button>
            </div>
          </div>
        )}
      </Show>
    </Show>
  );
}
